import React, { memo, forwardRef, useImperativeHandle } from "react";
import styles from "./PaymentPlanForm.module.scss";
import { useForm } from "react-hook-form";
import CommonFormFieldWrapper, { getFormInputErrors } from "../../../../../common/components/form-field-wrapper/CommonFormFieldWrapper";
import FormWrapper from "../../../../../common/components/form-wrapper/FormWrapper";
import CommonSelect, { CommonSelectItem } from "../../../../../common/components/select/CommonSelect";
import { PaymentPlan } from "../../../../../model/solar-recommendation-form/paymentPlan";

type Props = {
	initialPaymentPlan?: PaymentPlan;
	addPaymentPlan: (paymentPlan: PaymentPlan) => void;
	onBack: () => void;
};


const PaymentPlanForm = memo(
	forwardRef<{ savePayment: () => void }, Props>((props: Props, ref) => {
		const { addPaymentPlan, onBack } = props;

		const options = [
			new CommonSelectItem(3, "3 Months"),
			new CommonSelectItem(6, "6 Months"),
			new CommonSelectItem(9, "9 Months"),
			new CommonSelectItem(12, "12 Months"),
			new CommonSelectItem(15, "15 Months"),
			new CommonSelectItem(18, "18 Months"),
			new CommonSelectItem(21, "21 Months"),
			new CommonSelectItem(24, "24 Months"),
		];

		const {
			register,
			watch,
			formState: { errors, touchedFields, isValid, isSubmitted },
			setValue,
			handleSubmit,
		} = useForm<PaymentPlan>({
			defaultValues: props.initialPaymentPlan ?? {
				alternatePowerExpense: undefined,
				solarMonthlyRepayment: undefined,
				paymentPlan: undefined,
			},
			reValidateMode: "onChange",
			mode: "onTouched",
		});

		function submit(paymentPlan: PaymentPlan) {
			addPaymentPlan(paymentPlan);
		}

		useImperativeHandle(ref, () => ({
			savePayment: () => {
				handleSubmit(submit)();
			},
		}));

		return (
			<FormWrapper 
				titleSection={{ title: "Payment Plan", subtitle: "Let's help you get the best out of your budget.", buttonTitle: "Save & Continue" }}
				isNotValid={!isValid}
				isSubmitted={isSubmitted}
				onSaveAndContinue={handleSubmit(submit)}
				onBack={onBack}
			>
				<div className={styles.mainContainer}>
					<CommonFormFieldWrapper
						title="Alternate Power Expense"
						required
						errors={getFormInputErrors(errors.alternatePowerExpense?.message)}
						currency="NGN"
						description="How much do you want to spend on alternative power source (Generators)?"
					>
						<input
							{...register("alternatePowerExpense", {
								required: "Alternate Power Expense is required",
								min: { value: 0, message: "Amount must be greater than 0" },
							})}
							placeholder="Enter amount"
							type="number"
							min={0}
						/>
					</CommonFormFieldWrapper>
					<CommonFormFieldWrapper
						title="Solar Monthly Repayment"
						required
						errors={getFormInputErrors(errors.solarMonthlyRepayment?.message)}
						currency="NGN"
						description="How much do you want to pay monthly for the solar system?"
					>
						<input
							{...register("solarMonthlyRepayment", {
								required: "Solar Monthly Repayment is required",
								min: { value: 0, message: "Amount must be greater than 0" },
							})}
							placeholder="Enter amount"
							type="number"
							min={0}
						/>
					</CommonFormFieldWrapper>
					<div className={styles.containerElement}>
						<CommonFormFieldWrapper
							title="Payment Plan"
							required
							errors={getFormInputErrors(errors.paymentPlan?.message)}
							fieldTouched={touchedFields.paymentPlan}
							fieldValue={watch("paymentPlan")}
							description="How long do you want your payment plan to span?"
						>
							<CommonSelect
								items={options}
								placeholder="Select"
								searchable={false}
								selected={options.find(x => x.key === watch("paymentPlan"))}
								registerProp={register("paymentPlan", { required: "Payment Plan is required" })}
								onChange={e => setValue("paymentPlan", e ? e?.key : undefined, { shouldValidate: true, shouldTouch: true, shouldDirty: true })}
							/>
						</CommonFormFieldWrapper>
					</div>
				</div>
			</FormWrapper>
		);
	})
);

export default PaymentPlanForm;
